/* Vietiki product gallery */
if (!customElements.get('vk-gallery')) {
  customElements.define(
    'vk-gallery',
    class VkGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-vkg-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children).filter((el) => el.classList.contains('vkg__slide'));
        this.thumbsEl = this.querySelector('[data-vkg-thumbs]');
        this.thumbs = this.thumbsEl ? Array.from(this.thumbsEl.querySelectorAll('[data-vkg-thumb]')) : [];
        this.prevBtn = this.querySelector('[data-vkg-prev]');
        this.nextBtn = this.querySelector('[data-vkg-next]');
        this.tPrev = this.querySelector('[data-vkg-tprev]');
        this.tNext = this.querySelector('[data-vkg-tnext]');
        this.index = parseInt(this.dataset.start, 10) || 0;

        // Make sure the start slide is in view (inline script handles first paint)
        if (this.index > 0) this.track.scrollLeft = this.slides[this.index].offsetLeft;

        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        });
        this.prevBtn && this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn && this.nextBtn.addEventListener('click', () => this.go(this.index + 1));

        this.thumbs.forEach((btn) =>
          btn.addEventListener('click', () => this.go(parseInt(btn.dataset.vkgThumb, 10)))
        );
        if (this.thumbsEl) {
          this.thumbsEl.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
          this.tPrev && this.tPrev.addEventListener('click', () => this.scrollThumbs(-1));
          this.tNext && this.tNext.addEventListener('click', () => this.scrollThumbs(1));
          window.addEventListener('resize', () => this.updateThumbArrows(), { passive: true });
        }

        this.querySelectorAll('[data-vkg-open]').forEach((btn) =>
          btn.addEventListener('click', () => this.openLightbox(parseInt(btn.dataset.vkgOpen, 10)))
        );
        this.querySelectorAll('[data-vkg-ext]').forEach((btn) => btn.addEventListener('click', () => this.loadExternal(btn)));

        // Variant change -> show that variant's photo
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const media = event && event.data && event.data.variant && event.data.variant.featured_media;
            if (media) this.goToMediaId(media.id);
          });
        }

        // Video preview images: only load when the video is the current or a neighbouring slide
        this.posterVideos = Array.from(this.track.querySelectorAll('video[data-poster]'));
        this.loadPosters(this.index);

        this.setActive(this.index, false);
        this.updateThumbArrows();
        window.addEventListener('load', () => this.updateThumbArrows(), { once: true });
      }

      disconnectedCallback() {
        this.unsubscribe && this.unsubscribe();
      }

      onScroll() {
        const w = this.track.clientWidth || 1;
        const i = Math.round(this.track.scrollLeft / w);
        if (this.target != null) {
          // programmatic scroll in progress: wait until it lands
          if (i === this.target) this.target = null;
          return;
        }
        if (i !== this.index) this.setActive(i, true);
      }

      go(i) {
        if (i < 0 || i >= this.slides.length) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.target = i;
        clearTimeout(this.targetTimer);
        this.targetTimer = setTimeout(() => { this.target = null; }, 900);
        this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
        this.setActive(i, true);
      }

      goToMediaId(id) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
        if (i > -1) this.go(i);
      }

      setActive(i, scrollThumb) {
        const prev = this.index;
        this.index = i;
        this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
        this.thumbs.forEach((t, n) => {
          const on = n === i;
          t.classList.toggle('is-active', on);
          on ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current');
        });
        if (this.prevBtn) this.prevBtn.disabled = i === 0;
        if (this.nextBtn) this.nextBtn.disabled = i === this.slides.length - 1;
        if (scrollThumb) this.revealThumb(i);
        this.handleVideo(prev, i);
      }

      revealThumb(i) {
        const t = this.thumbs[i];
        if (!t || !this.thumbsEl) return;
        const box = this.thumbsEl;
        const left = t.offsetLeft - box.offsetLeft;
        if (left < box.scrollLeft || left + t.offsetWidth > box.scrollLeft + box.clientWidth) {
          box.scrollTo({ left: Math.max(0, left - (box.clientWidth - t.offsetWidth) / 2) });
        }
      }

      scrollThumbs(dir) {
        this.thumbsEl.scrollBy({ left: dir * this.thumbsEl.clientWidth * 0.8 });
      }

      updateThumbArrows() {
        if (!this.thumbsEl) return;
        const b = this.thumbsEl;
        const max = b.scrollWidth - b.clientWidth;
        if (this.tPrev) this.tPrev.hidden = b.scrollLeft <= 2;
        if (this.tNext) this.tNext.hidden = b.scrollLeft >= max - 2;
      }

      loadPosters(i) {
        if (!this.posterVideos || !this.posterVideos.length) return;
        this.posterVideos = this.posterVideos.filter((v) => {
          const n = this.slides.indexOf(v.closest('.vkg__slide'));
          if (Math.abs(n - i) > 1) return true;
          v.poster = v.dataset.poster;
          v.removeAttribute('data-poster');
          return false;
        });
      }

      handleVideo(prev, next) {
        this.loadPosters(next);
        const pv = this.slides[prev] && this.slides[prev].querySelector('video');
        if (pv && prev !== next) pv.pause();
        const nv = this.slides[next] && this.slides[next].querySelector('video');
        if (nv) {
          nv.muted = true;
          const p = nv.play();
          if (p && p.catch) p.catch(() => {});
        }
        if (prev !== next) {
          const frame = this.slides[prev] && this.slides[prev].querySelector('iframe');
          if (frame) {
            frame.remove();
            const b = this.slides[prev].querySelector('[data-vkg-ext]');
            if (b) b.hidden = false;
          }
        }
      }

      loadExternal(btn) {
        const slide = btn.closest('.vkg__slide');
        const tpl = slide.querySelector('[data-vkg-ext-tpl]');
        if (!tpl) return;
        slide.appendChild(tpl.content.cloneNode(true));
        btn.hidden = true;
      }

      /* ---------- Lightbox ---------- */
      buildLightbox() {
        const lb = document.createElement('div');
        lb.className = 'vkg-lb';
        lb.hidden = true;
        lb.setAttribute('role', 'dialog');
        lb.setAttribute('aria-modal', 'true');
        lb.setAttribute('aria-label', this.getAttribute('aria-label') || '');
        const arrow = (d) =>
          `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
        lb.innerHTML = `
          <div class="vkg-lb__bar">
            <span class="vkg-lb__count" aria-live="polite"></span>
            <div class="vkg-lb__tools">
              <button type="button" class="vkg-lb__btn" data-lb-zoom aria-label="${this.dataset.zoomLabel || 'Zoom'}">${arrow('M11 4a7 7 0 1 0 0 14a7 7 0 0 0 0-14zM20 20l-4-4M8 11h6M11 8v6')}</button>
              <button type="button" class="vkg-lb__btn" data-lb-close aria-label="${this.dataset.closeLabel || 'Close'}">${arrow('M6 6l12 12M18 6L6 18')}</button>
            </div>
          </div>
          <button type="button" class="vkg-lb__btn vkg-lb__nav vkg-lb__nav--prev" data-lb-prev aria-label="${this.prevBtn ? this.prevBtn.getAttribute('aria-label') : ''}">${arrow('M19 12H5M11 6l-6 6 6 6')}</button>
          <button type="button" class="vkg-lb__btn vkg-lb__nav vkg-lb__nav--next" data-lb-next aria-label="${this.nextBtn ? this.nextBtn.getAttribute('aria-label') : ''}">${arrow('M5 12h14M13 6l6 6-6 6')}</button>
          <div class="vkg-lb__track"></div>`;
        const track = lb.querySelector('.vkg-lb__track');
        // Build full-size slides from the gallery's own images (Shopify CDN: change the width parameter)
        const sized = (url, w) => (/[?&]width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, `$1width=${w}`) : `${url}${url.includes('?') ? '&' : '?'}width=${w}`);
        this.slides.forEach((slide, n) => {
          const src = slide.querySelector('.vkg__zoom-btn img');
          if (!src) return;
          const url = src.getAttribute('src') || src.getAttribute('data-vkg-src');
          const wrap = document.createElement('div');
          wrap.className = 'vkg-lb__slide';
          wrap.dataset.index = n;
          const img = document.createElement('img');
          img.className = 'vkg-lb__img';
          img.loading = 'lazy';
          img.draggable = false;
          img.alt = src.alt || '';
          if (src.getAttribute('width')) img.width = src.getAttribute('width');
          if (src.getAttribute('height')) img.height = src.getAttribute('height');
          img.sizes = '100vw';
          img.srcset = [1080, 1600, 2048].map((w) => `${sized(url, w)} ${w}w`).join(', ');
          img.src = sized(url, 2048);
          wrap.appendChild(img);
          track.appendChild(wrap);
        });
        document.body.appendChild(lb);

        this.lb = lb;
        this.lbTrack = track;
        this.lbSlides = Array.from(track.children);
        this.lbIndex = 0;

        lb.querySelector('[data-lb-close]').addEventListener('click', () => this.closeLightbox());
        lb.querySelector('[data-lb-prev]').addEventListener('click', () => this.lbGo(this.lbIndex - 1));
        lb.querySelector('[data-lb-next]').addEventListener('click', () => this.lbGo(this.lbIndex + 1));
        lb.querySelector('[data-lb-zoom]').addEventListener('click', () => {
          const img = this.lbSlides[this.lbIndex] && this.lbSlides[this.lbIndex].querySelector('img');
          if (img) this.toggleZoom(img, null);
        });
        track.addEventListener('scroll', () => {
          const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
          if (i !== this.lbIndex) this.lbSetActive(i);
        }, { passive: true });
        track.addEventListener('click', (e) => {
          const img = e.target.closest('.vkg-lb__img');
          if (img && !this.lbDragged) this.toggleZoom(img, e);
        });
        track.addEventListener('pointermove', (e) => this.panZoom(e));
        track.addEventListener('pointerdown', () => { this.lbDragged = false; });
        this.onLbKey = (e) => {
          if (e.key === 'Escape') this.closeLightbox();
          if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
          if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
          if (e.key === 'Tab') this.trapFocus(e);
        };
      }

      openLightbox(slideIndex) {
        if (!this.lb) this.buildLightbox();
        if (!this.lbSlides.length) return;
        let i = this.lbSlides.findIndex((s) => parseInt(s.dataset.index, 10) === slideIndex);
        if (i < 0) i = 0;
        this.lastFocus = document.activeElement;
        this.lb.hidden = false;
        document.documentElement.classList.add('vkg-lock');
        document.addEventListener('keydown', this.onLbKey);
        const img = this.lbSlides[i].querySelector('img');
        if (img) img.loading = 'eager';
        this.lbTrack.scrollLeft = this.lbSlides[i].offsetLeft;
        this.lbSetActive(i);
        this.lb.querySelector('[data-lb-close]').focus();
      }

      closeLightbox() {
        if (!this.lb || this.lb.hidden) return;
        this.resetZoom();
        this.lb.hidden = true;
        document.documentElement.classList.remove('vkg-lock');
        document.removeEventListener('keydown', this.onLbKey);
        // keep the main gallery on the photo the visitor ended on
        const s = this.lbSlides[this.lbIndex];
        if (s) {
          const main = parseInt(s.dataset.index, 10);
          this.track.scrollLeft = this.slides[main].offsetLeft;
          this.setActive(main, true);
        }
        this.lastFocus && this.lastFocus.focus && this.lastFocus.focus();
      }

      lbGo(i) {
        if (i < 0 || i >= this.lbSlides.length) return;
        this.resetZoom();
        this.lbTrack.scrollTo({ left: this.lbSlides[i].offsetLeft, behavior: 'smooth' });
        this.lbSetActive(i);
      }

      lbSetActive(i) {
        if (this.lbIndex !== i) this.resetZoom();
        this.lbIndex = i;
        const of = this.dataset.ofLabel || '/';
        this.lb.querySelector('.vkg-lb__count').textContent = `${i + 1} / ${this.lbSlides.length}`;
        this.lb.querySelector('.vkg-lb__count').setAttribute('aria-label', `${i + 1} ${of} ${this.lbSlides.length}`);
        this.lb.querySelector('[data-lb-prev]').disabled = i === 0;
        this.lb.querySelector('[data-lb-next]').disabled = i === this.lbSlides.length - 1;
        [i - 1, i + 1].forEach((n) => {
          const im = this.lbSlides[n] && this.lbSlides[n].querySelector('img');
          if (im) im.loading = 'eager';
        });
      }

      toggleZoom(img, e) {
        if (img.classList.contains('is-zoomed')) return this.resetZoom();
        this.resetZoom();
        this.zoomRect = img.getBoundingClientRect();
        img.classList.add('is-zoomed');
        this.lbTrack.classList.add('is-zoomed');
        this.zoomed = img;
        if (e) this.panZoom(e);
      }

      panZoom(e) {
        const img = this.zoomed;
        if (!img) return;
        if (e.pointerType === 'touch' && e.buttons) this.lbDragged = true;
        const r = this.zoomRect || img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
        img.style.transformOrigin = `${x}% ${y}%`;
      }

      resetZoom() {
        if (!this.zoomed) return;
        this.zoomed.classList.remove('is-zoomed');
        this.zoomed.style.transformOrigin = '';
        this.lbTrack.classList.remove('is-zoomed');
        this.zoomed = null;
      }

      trapFocus(e) {
        const f = Array.from(this.lb.querySelectorAll('button:not([disabled])'));
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (!this.lb.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  );
}